/**
 * BookSeriesEditModal.jsx — In-place edit modal for a Book Series + its
 * episodes. Feb 12 2026 user spec: *"edits to Book series has not been
 * wired as earlier instructed. please fix that."*
 *
 * Two modes:
 *   - mode='series'  → edit series title, author, description, genre, cover
 *   - mode='episode' → edit episode title + body
 *
 * Wires to the PUT endpoints added in iter 73:
 *   PUT /api/text-series/{series_id}
 *   PUT /api/text-series/{series_id}/episodes/{episode_id}
 *
 * Quota:
 *   - High King + Kings: unlimited edits.
 *   - Writers: 1 edit per item; the EditQuotaPill in the header shows
 *     remaining count + "Request edit" CTA when exhausted.
 */
import React, { useState, useEffect } from 'react';
import { X, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../utils/authUtils';
import EditQuotaPill from './EditQuotaPill';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BookSeriesEditModal = ({ mode, series, episode, onClose, onSaved }) => {
  const isEpisode = mode === 'episode' && !!episode;
  const target = isEpisode ? episode : series;

  const [title, setTitle] = useState(target.title || '');
  const [author, setAuthor] = useState(series?.author || '');
  const [description, setDescription] = useState(series?.description || '');
  const [genre, setGenre] = useState(series?.genre || '');
  const [bodyText, setBodyText] = useState(episode?.body_text || '');
  const [cover, setCover] = useState(null);
  const [saving, setSaving] = useState(false);
  // Feb 2026: AI-generated cover from description (Nano Banana).
  const [genCoverBusy, setGenCoverBusy] = useState(false);
  const [aiCoverPreview, setAiCoverPreview] = useState(null);
  // Iter 79 fix: the listing endpoint returns lightweight episode
  // metadata (title, word_count, episode_number) but NOT body_text —
  // so the parent's `episode` prop arrives without the body. Fetch
  // the full episode on mount when we're editing one, so the textarea
  // pre-populates with the previous content instead of being empty.
  const [hydrating, setHydrating] = useState(isEpisode && !episode?.body_text);

  useEffect(() => {
    if (!isEpisode) return;
    if (episode?.body_text) return; // already have it, skip fetch
    let alive = true;
    (async () => {
      try {
        const r = await authFetch(
          `${BACKEND_URL}/api/text-series/${series.series_id}/episodes/${episode.episode_id}`,
        );
        if (!r.ok) throw new Error('load failed');
        const d = await r.json();
        const ep = d?.episode || d;
        if (!alive) return;
        // Hydrate all fields the writer would want editable, using
        // the fresh copy from the server (in case the listing was
        // stale).
        if (ep?.title && !title) setTitle(ep.title);
        setBodyText(ep?.body_text || '');
      } catch (_e) {
        toast.error('Could not load episode body — try again.');
      } finally {
        if (alive) setHydrating(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEpisode, episode?.episode_id, series?.series_id]);

  const save = async () => {
    // Iter 79: enforce 1000-5000 word window on episode edits too.
    // Series-level edits (title/description/cover) don't touch word
    // count, so only guard when we're actually replacing body text.
    if (isEpisode && bodyText.trim()) {
      const wc = bodyText.trim().split(/\s+/).filter(Boolean).length;
      if (wc < 1000) {
        toast.error(`Episode too short — minimum 1,000 words (yours: ${wc.toLocaleString()}).`);
        return;
      }
      if (wc > 5000) {
        toast.error(`Episode too long — maximum 5,000 words (yours: ${wc.toLocaleString()}). Split into multiple episodes.`);
        return;
      }
    }
    setSaving(true);
    try {
      const fd = new FormData();
      if (isEpisode) {
        if (title.trim()) fd.append('title', title.trim());
        if (bodyText.trim()) fd.append('body_text', bodyText.trim());
      } else {
        if (title.trim()) fd.append('title', title.trim());
        if (author.trim()) fd.append('author', author.trim());
        if (description !== undefined) fd.append('description', description);
        if (genre !== undefined) fd.append('genre', genre);
        if (cover) fd.append('cover_image', cover);
      }

      const url = isEpisode
        ? `${BACKEND_URL}/api/text-series/${series.series_id}/episodes/${episode.episode_id}`
        : `${BACKEND_URL}/api/text-series/${series.series_id}`;

      const r = await authFetch(url, { method: 'PUT', body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || 'Save failed');
        return;
      }
      toast.success(isEpisode ? 'Episode updated' : 'Series updated');
      onSaved?.(d);
      onClose?.();
    } finally {
      setSaving(false);
    }
  };

  // For the EditQuotaPill, derive the item_kind.
  const quotaKind = isEpisode ? 'text_episode' : 'text_series';
  const quotaId = isEpisode ? episode.episode_id : series.series_id;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm grid place-items-center p-3"
      onClick={onClose}
      data-testid="book-series-edit-modal"
    >
      <div
        className="w-full max-w-xl max-h-[92vh] flex flex-col rounded-2xl bg-[#12131A] border border-white/10 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <h3 className="font-display text-xl font-black text-white tracking-tight truncate">
              Edit {isEpisode ? 'episode' : 'series'}<span className="text-amber-300">.</span>
            </h3>
            <EditQuotaPill
              itemKind={quotaKind}
              itemId={quotaId}
              testidPrefix={`book-series-edit-quota-${quotaId}`}
            />
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 grid place-items-center flex-shrink-0"
            aria-label="Close"
            data-testid="book-series-edit-close"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest font-black text-white/55">Title</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 w-full rounded-lg bg-[#0B0C10] border border-white/10 px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-amber-400/60"
              data-testid="book-series-edit-title"
            />
          </label>

          {!isEpisode && (
            <>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-black text-white/55">Author</span>
                <input
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  className="mt-1 w-full rounded-lg bg-[#0B0C10] border border-white/10 px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-amber-400/60"
                  data-testid="book-series-edit-author"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-black text-white/55">Description</span>
                <textarea
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="mt-1 w-full rounded-lg bg-[#0B0C10] border border-white/10 px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-amber-400/60 resize-y"
                  data-testid="book-series-edit-description"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-black text-white/55">Genre</span>
                <input
                  value={genre}
                  onChange={(e) => setGenre(e.target.value)}
                  className="mt-1 w-full rounded-lg bg-[#0B0C10] border border-white/10 px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-amber-400/60"
                  data-testid="book-series-edit-genre"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-black text-white/55">Replace cover image</span>
                <div className="mt-1 flex items-start gap-2 flex-wrap">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0] || null;
                      setCover(f);
                      if (f) {
                        const fr = new FileReader();
                        fr.onload = () => setAiCoverPreview(fr.result);
                        fr.readAsDataURL(f);
                      }
                    }}
                    className="text-xs text-white/70 file:mr-3 file:px-3 file:py-1 file:rounded file:border-0 file:bg-amber-400/15 file:text-amber-200"
                    data-testid="book-series-edit-cover"
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      const desc = (description || '').trim();
                      if (desc.length < 10) {
                        toast.error('Add a description first (10+ chars).');
                        return;
                      }
                      setGenCoverBusy(true);
                      try {
                        const r = await authFetch(`${BACKEND_URL}/api/book-series/generate-cover`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            title, author, genre, description: desc, content_type: 'ebook',
                          }),
                        });
                        const d = await r.json().catch(() => ({}));
                        if (!r.ok || !d.cover_url) throw new Error(d.detail || `HTTP ${r.status}`);
                        const url = d.cover_url.startsWith('http') ? d.cover_url : `${BACKEND_URL}${d.cover_url}`;
                        const img = await fetch(url);
                        const blob = await img.blob();
                        const fname = d.cover_url.split('/').pop() || 'ai_cover.png';
                        const file = new File([blob], fname, { type: blob.type || 'image/png' });
                        setCover(file);
                        setAiCoverPreview(URL.createObjectURL(blob));
                        toast.success('AI cover ready.');
                      } catch (e) {
                        toast.error(e.message || 'Cover gen failed');
                      } finally {
                        setGenCoverBusy(false);
                      }
                    }}
                    disabled={genCoverBusy}
                    className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border border-fuchsia-400/40 bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-200 inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="book-series-edit-ai-cover"
                  >
                    {genCoverBusy ? '…' : '✨'} Generate with AI
                  </button>
                </div>
                {aiCoverPreview && (
                  <img src={aiCoverPreview} alt="cover preview" className="mt-2 rounded-lg w-24 h-24 object-cover border border-white/10" />
                )}
              </label>
            </>
          )}

          {isEpisode && (
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest font-black text-white/55">Body</span>
              {hydrating ? (
                <div
                  className="mt-1 w-full h-[300px] rounded-lg bg-[#0B0C10] border border-white/10 flex items-center justify-center"
                  data-testid="book-series-edit-body-loading"
                >
                  <Loader2 className="w-5 h-5 animate-spin text-white/40" />
                  <span className="ml-2 text-xs text-white/40">Loading previous episode…</span>
                </div>
              ) : (
                <textarea
                  rows={12}
                  value={bodyText}
                  onChange={(e) => setBodyText(e.target.value)}
                  className="mt-1 w-full rounded-lg bg-[#0B0C10] border border-white/10 px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-amber-400/60 resize-y"
                  data-testid="book-series-edit-body"
                />
              )}
              {(() => {
                const wc = bodyText.trim() ? bodyText.trim().split(/\s+/).filter(Boolean).length : 0;
                const tooShort = wc > 0 && wc < 1000;
                const tooLong = wc > 5000;
                const ok = wc >= 1000 && wc <= 5000;
                const color = ok
                  ? 'text-emerald-300'
                  : (tooShort || tooLong) ? 'text-red-300' : 'text-white/40';
                let msg = 'min 1,000 · max 5,000';
                if (tooShort) msg = `need ${(1000 - wc).toLocaleString()} more`;
                else if (tooLong) msg = `over by ${(wc - 5000).toLocaleString()}`;
                else if (ok) msg = 'ready to publish';
                return (
                  <span
                    className={`block text-[10px] mt-1 tabular-nums font-black uppercase tracking-widest ${color}`}
                    data-testid="book-series-edit-word-count"
                  >
                    {wc.toLocaleString()} words · {msg}
                  </span>
                );
              })()}
            </label>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-white/10">
          <button
            type="button"
            onClick={save}
            disabled={saving || hydrating}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black font-black px-6 py-3 text-sm uppercase tracking-widest"
            data-testid="book-series-edit-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </footer>
      </div>
    </div>
  );
};

export default BookSeriesEditModal;
